import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { EmployeesService } from "../../../services/employees.service";
import { warehouseService } from "../../../services/warehouse.service";
import { locationService } from "../../../services/location.service";

const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[14px] bg-white ${className}`}
      style={{ border: `1px solid ${BORDER}` }}
    >
      {children}
    </div>
  );
}

function Input({ label, required = false, ...props }) {
  return (
    <label className="block">
      <div className="mb-1 text-[13px] font-semibold text-slate-600">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </div>
      <input
        {...props}
        className="h-[42px] w-full rounded-[10px] border bg-white px-3 text-[14px] text-slate-700 outline-none focus:ring-2 focus:ring-[#f4d6db]"
        style={{ borderColor: BORDER }}
      />
    </label>
  );
}

function Textarea({ label, rows = 3, ...props }) {
  return (
    <label className="block">
      <div className="mb-1 text-[13px] font-semibold text-slate-600">
        {label}
      </div>
      <textarea
        {...props}
        rows={rows}
        className="w-full rounded-[10px] border bg-white px-3 py-2 text-[14px] text-slate-700 outline-none focus:ring-2 focus:ring-[#f4d6db]"
        style={{ borderColor: BORDER }}
      />
    </label>
  );
}

function Select({ label, required = false, children, ...props }) {
  return (
    <label className="block">
      <div className="mb-1 text-[13px] font-semibold text-slate-600">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </div>
      <div className="relative">
        <select
          {...props}
          className="h-[42px] w-full appearance-none rounded-[10px] border bg-white px-3 pr-10 text-[14px] text-slate-700 outline-none focus:ring-2 focus:ring-[#f4d6db]"
          style={{ borderColor: BORDER }}
        >
          {children}
        </select>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
      </div>
    </label>
  );
}

function Toggle({ label, checked, onChange, hint, disabled = false }) {
  return (
    <div className="rounded-[12px] border p-3" style={{ borderColor: BORDER }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-slate-700">{label}</div>
          {hint ? <div className="mt-1 text-[12px] text-slate-500">{hint}</div> : null}
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(!checked)}
          className={
            "relative h-[28px] w-[52px] rounded-full transition " +
            (checked ? "bg-[#d86a78]" : "bg-slate-200") +
            (disabled ? " cursor-not-allowed opacity-70" : "")
          }
        >
          <span
            className={
              "absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white shadow transition " +
              (checked ? "left-[27px]" : "left-[3px]")
            }
          />
        </button>
      </div>
    </div>
  );
}

function MultiCheckbox({ label, options = [], selected = [], onChange, hint }) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleOne = (value) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  };

  return (
    <div>
      <div className="mb-1 text-[13px] font-semibold text-slate-600">{label}</div>
      {hint ? <div className="mb-2 text-[12px] text-slate-500">{hint}</div> : null}

      <div
        className="max-h-[220px] overflow-auto rounded-[12px] border p-2"
        style={{ borderColor: BORDER }}
      >
        {options.length === 0 ? (
          <div className="p-2 text-[13px] text-slate-400">No options available</div>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 rounded-[10px] border px-3 py-2"
                style={{ borderColor: BORDER }}
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(opt.value)}
                  onChange={() => toggleOne(opt.value)}
                />
                <span className="text-[13px] text-slate-700">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


const ADMIN_PERMISSION_MODULES = [
  {
    id: "dashboard",
    title: "Dashboard",
    hint: "Dashboard home page access.",
    match: (key) => key === "dashboard.view" || key === "admin.panel.access",
  },
  {
    id: "employees",
    title: "Employees",
    hint: "Employee list, create, edit, delete and onboarding approval.",
    match: (key) => key.startsWith("employees.") || key.startsWith("admin.employees."),
  },
  {
    id: "roles",
    title: "Roles & Permissions",
    hint: "Role creation and permission management.",
    match: (key) => key.startsWith("rbac."),
  },
  {
    id: "warehouses",
    title: "Warehouse Management",
    hint: "Warehouse list, create, edit, delete and approval.",
    match: (key) => key.startsWith("warehouses."),
  },
  {
    id: "products",
    title: "Products",
    hint: "Product catalog access and product approval actions.",
    match: (key) => key.startsWith("products."),
  },
  {
    id: "app-layout",
    title: "App Layout",
    hint: "Category order and mobile app layout manager.",
    match: (key) => key.startsWith("admin.app_layout."),
  },
  {
    id: "driver-wallet",
    title: "Driver Wallet",
    hint: "Driver wallet and withdrawal requests.",
    match: (key) => key.startsWith("admin.driver_wallet."),
  },
  {
    id: "settings",
    title: "Settings",
    hint: "Admin settings and runtime settings.",
    match: (key) => key.startsWith("admin.settings."),
  },
  {
    id: "broadcasts",
    title: "Broadcast Messages",
    hint: "Create and manage user/warehouse/driver broadcasts.",
    match: (key) => key.startsWith("admin.broadcasts."),
  },
  {
    id: "posters",
    title: "Posters",
    hint: "Poster/banner upload and management.",
    match: (key) => key.startsWith("admin.posters."),
  },
  {
    id: "short-videos",
    title: "Short Videos",
    hint: "Short video ads, scheduling, warehouse targeting and history.",
    match: (key) => key.startsWith("admin.short_videos."),
  },
  {
    id: "reports",
    title: "Reports",
    hint: "Admin reports, analytics and exports.",
    match: (key) => key.startsWith("admin.reports."),
  },
  {
    id: "support",
    title: "Customer Support",
    hint: "Full access to support inbox, replies, assignments, settings and chat clearing.",
    match: (key) => key === "admin.support",
  },
];

const WAREHOUSE_PERMISSION_MODULES = [
  {
    id: "warehouse-panel",
    title: "Warehouse Panel",
    hint: "Basic warehouse dashboard access.",
    match: (key) => key === "warehouse.panel.access" || key === "wh.panel.access",
  },
  {
    id: "warehouse-orders",
    title: "Customer Orders",
    hint: "Warehouse customer order actions.",
    match: (key) => key.includes("order"),
  },
  {
    id: "warehouse-inventory",
    title: "Stock & Inventory",
    hint: "Stock, inventory, inbound and expired stock actions.",
    match: (key) =>
      key.includes("inventory") ||
      key.includes("stock") ||
      key.includes("inbound") ||
      key.includes("receipt") ||
      key.includes("expired"),
  },
  {
    id: "warehouse-products",
    title: "Warehouse Products",
    hint: "Warehouse-side product visibility and product details.",
    match: (key) => key.includes("product") || key.includes("catalog"),
  },
  {
    id: "warehouse-employees",
    title: "Warehouse Employees",
    hint: "Warehouse employee list and assignment actions.",
    match: (key) => key.includes("employee"),
  },
  {
    id: "warehouse-notifications",
    title: "Notifications",
    hint: "Warehouse notifications read and update actions.",
    match: (key) => key.includes("notification"),
  },
];

const PERMISSION_ACTION_LABELS = {
  view: "View",
  read: "View",
  create: "Create",
  update: "Edit",
  edit: "Edit",
  delete: "Delete",
  approve: "Approve",
  manage: "Manage",
  export: "Export",
  access: "Access",
};

const FRIENDLY_PERMISSION_LABELS = {
  "admin.panel.access": "Open Admin Panel",
  "dashboard.view": "View Dashboard",

  "employees.view": "View Employees",
  "employees.create": "Create Employee",
  "employees.update": "Edit Employee",
  "employees.delete": "Delete Employee",
  "employees.approve": "Approve Employee Onboarding",
  "admin.employees.read": "View Admin Employees",
  "admin.employees.create": "Create Admin Employee",
  "admin.employees.update": "Edit Admin Employee",
  "admin.employees.delete": "Delete Admin Employee",

  "wh.employees.read": "View Warehouse Employees",
  "wh.employees.create": "Create Warehouse Employee",
  "wh.employees.update": "Edit Warehouse Employee",
  "wh.employees.delete": "Delete Warehouse Employee",
  "wh.employees.approve": "Approve Warehouse Employee Onboarding",
  "warehouse.employees.read": "View Warehouse Employees",
  "warehouse.employees.create": "Create Warehouse Employee",
  "warehouse.employees.update": "Edit Warehouse Employee",
  "warehouse.employees.delete": "Delete Warehouse Employee",
  "warehouse.employees.approve": "Approve Warehouse Employee Onboarding",

  "rbac.permissions.read": "View Permission List",
  "rbac.roles.read": "View Roles",
  "rbac.roles.create": "Create Role",
  "rbac.roles.delete": "Delete Role",

  "warehouses.view": "View Warehouses",
  "warehouses.create": "Create Warehouse",
  "warehouses.update": "Edit Warehouse",
  "warehouses.delete": "Delete Warehouse",
  "warehouses.approve": "Approve Warehouse Actions",

  "products.view": "View Products",
  "products.create": "Create Product",
  "products.update": "Edit Product",
  "products.delete": "Delete Product",
  "products.approve": "Approve Product Actions",

  "admin.app_layout.read": "View App Layout",
  "admin.app_layout.manage": "Manage App Layout",
  "admin.driver_wallet.read": "View Driver Wallet",
  "admin.driver_wallet.manage": "Manage Driver Wallet Requests",
  "admin.settings.read": "View Settings",
  "admin.settings.update": "Update Settings",
  "admin.broadcasts.read": "View Broadcasts",
  "admin.broadcasts.manage": "Manage Broadcasts",
  "admin.posters.read": "View Posters",
  "admin.posters.manage": "Manage Posters",
  "admin.short_videos.read": "View Short Videos",
  "admin.short_videos.manage": "Manage Short Videos",
  "admin.reports.read": "View Reports",
  "admin.reports.export": "Export Reports",
  "admin.support": "Support",
};

function titleCase(value) {
  return String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function getFriendlyPermissionLabel(option) {
  const key = String(option?.key || "").trim();
  if (FRIENDLY_PERMISSION_LABELS[key]) return FRIENDLY_PERMISSION_LABELS[key];

  const parts = key.split(".").filter(Boolean);
  const action = parts[parts.length - 1] || "";
  const target = parts.slice(0, -1).filter((x) => !["admin", "wh"].includes(x)).join(" ");
  const actionLabel = PERMISSION_ACTION_LABELS[action] || titleCase(action);

  if (target) return `${actionLabel} ${titleCase(target)}`;
  return titleCase(option?.description || option?.label || key || "Permission");
}

function getPermissionActionBadge(option) {
  const key = String(option?.key || "").trim();
  if (key === "admin.support") return "Full Access";
  const action = key.split(".").filter(Boolean).pop();
  return PERMISSION_ACTION_LABELS[action] || titleCase(action || "Access");
}

function buildPermissionGroups(options = [], modules = []) {
  const used = new Set();
  const groups = modules
    .map((mod) => {
      const items = options
        .filter((opt) => {
          const key = String(opt?.key || "").toLowerCase();
          return mod.match(key);
        })
        .sort((a, b) => getFriendlyPermissionLabel(a).localeCompare(getFriendlyPermissionLabel(b)));

      items.forEach((item) => used.add(item.value));
      return { ...mod, items };
    })
    .filter((group) => group.items.length > 0);

  const others = options
    .filter((opt) => !used.has(opt.value))
    .sort((a, b) => getFriendlyPermissionLabel(a).localeCompare(getFriendlyPermissionLabel(b)));

  if (others.length > 0) {
    groups.push({
      id: "other",
      title: "Other Permissions",
      hint: "Extra permissions that are not linked to a fixed module yet.",
      items: others,
    });
  }

  return groups;
}

function ModulePermissionPicker({ label, options = [], selected = [], onChange, hint, modules = [] }) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const groups = useMemo(() => buildPermissionGroups(options, modules), [options, modules]);

  const toggleOne = (value) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  };

  const toggleGroup = (items) => {
    const ids = items.map((item) => item.value);
    const allSelected = ids.every((id) => selectedSet.has(id));
    const next = new Set(selectedSet);

    ids.forEach((id) => {
      if (allSelected) next.delete(id);
      else next.add(id);
    });

    onChange(Array.from(next));
  };

  const selectedCount = options.filter((opt) => selectedSet.has(opt.value)).length;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-[13px] font-semibold text-slate-700">{label}</div>
          {hint ? <div className="mt-1 text-[12px] text-slate-500">{hint}</div> : null}
        </div>
        <div className="rounded-full bg-rose-50 px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
          {selectedCount}/{options.length} selected
        </div>
      </div>

      <div
        className="max-h-[390px] overflow-auto rounded-[16px] border bg-slate-50/50 p-3"
        style={{ borderColor: BORDER }}
      >
        {groups.length === 0 ? (
          <div className="rounded-[12px] bg-white p-3 text-[13px] text-slate-400">
            No permissions available
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const groupIds = group.items.map((item) => item.value);
              const groupSelectedCount = groupIds.filter((id) => selectedSet.has(id)).length;
              const allSelected = groupIds.length > 0 && groupSelectedCount === groupIds.length;
              const partiallySelected = groupSelectedCount > 0 && !allSelected;

              return (
                <div
                  key={group.id}
                  className="rounded-[14px] border bg-white p-3 shadow-sm"
                  style={{ borderColor: "#f6d2d7" }}
                >
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="text-[14px] font-bold text-slate-800">{group.title}</div>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                          {groupSelectedCount}/{group.items.length}
                        </span>
                      </div>
                      {group.hint ? <div className="mt-1 text-[12px] text-slate-500">{group.hint}</div> : null}
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleGroup(group.items)}
                      className="rounded-full border px-3 py-1 text-[12px] font-semibold text-slate-600 hover:bg-rose-50"
                      style={{ borderColor: BORDER }}
                    >
                      {allSelected ? "Remove all" : partiallySelected ? "Select remaining" : "Select all"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {group.items.map((opt) => {
                      const checked = selectedSet.has(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={
                            "flex cursor-pointer items-start gap-3 rounded-[12px] border p-3 transition " +
                            (checked ? "bg-rose-50" : "bg-white hover:bg-slate-50")
                          }
                          style={{ borderColor: checked ? PINK : BORDER }}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            onChange={() => toggleOne(opt.value)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13px] font-semibold text-slate-700">
                              {getFriendlyPermissionLabel(opt)}
                            </span>
                            <span className="mt-1 inline-flex rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                              {getPermissionActionBadge(opt)}
                            </span>
                            {opt.description ? (
                              <span className="mt-1 block text-[11px] leading-4 text-slate-400">
                                {opt.description}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function normalizeArray(input) {
  if (Array.isArray(input)) return input;
  if (Array.isArray(input?.data)) return input.data;
  if (Array.isArray(input?.items)) return input.items;
  if (Array.isArray(input?.rows)) return input.rows;
  if (Array.isArray(input?.data?.items)) return input.data.items;
  if (Array.isArray(input?.data?.rows)) return input.data.rows;
  return [];
}

function normalizeRoleOption(r) {
  const rawPermissions =
    r?.permissions ||
    r?.permissionKeys ||
    r?.permission_keys ||
    r?.permissionIds ||
    r?.permission_ids ||
    [];

  return {
    id: String(r?.id || r?.roleId || r?._id || ""),
    name: String(r?.name || r?.role || r?.label || ""),
    appliesTo: String(r?.appliesTo || r?.applies_to || r?.scope || "both"),
    permissions: Array.isArray(rawPermissions)
      ? rawPermissions.map((p) =>
        typeof p === "string"
          ? p
          : String(
            p?.key ||
            p?.permKey ||
            p?.perm_key ||
            p?.name ||
            p?.id ||
            ""
          )
      ).filter(Boolean)
      : [],
  };
}

function normalizePermissionOption(p) {
  return {
    id: String(p?.id || p?.permissionId || p?._id || ""),
    key: String(p?.permKey || p?.perm_key || p?.key || p?.name || ""),
    label: String(p?.name || p?.permKey || p?.perm_key || p?.key || p?.id || ""),
    description: String(p?.description || p?.desc || p?.details || ""),
    appliesTo: String(p?.appliesTo || p?.applies_to || "both"),
  };
}

function normalizeWarehouseOption(w) {
  return {
    id: String(w?.id || w?._id || ""),
    name: String(w?.name || w?.warehouseName || w?.code || w?.slug || w?.id || ""),
  };
}

function StatusPopup({ open, type = "error", title, message, onClose }) {
  if (!open) return null;

  const isSuccess = type === "success";

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="w-full max-w-md rounded-[16px] bg-white p-6 shadow-xl"
        style={{ border: `1px solid ${BORDER}` }}
      >
        <div className="flex justify-center">
          <div
            className={`flex h-[72px] w-[72px] items-center justify-center rounded-full border-4 ${isSuccess ? "border-green-200" : "border-rose-200"
              }`}
          >
            <div
              className={`text-[30px] font-bold ${isSuccess ? "text-green-500" : "text-rose-500"
                }`}
            >
              {isSuccess ? "✓" : "!"}
            </div>
          </div>
        </div>

        <div className="mt-4 text-center">
          <h3 className="text-[20px] font-bold text-slate-800">{title}</h3>
          <p className="mt-2 text-[14px] text-slate-500">{message}</p>
        </div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="h-[42px] min-w-[120px] rounded-[10px] px-5 text-[14px] font-semibold text-white"
            style={{ backgroundColor: isSuccess ? "#22c55e" : PINK }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || "").trim());
}

function isValidPhone(phone) {
  return /^\d{10}$/.test(String(phone || "").trim());
}

function isValidPincode(pincode) {
  return /^\d{6}$/.test(String(pincode || "").trim());
}

function isAlphabeticText(value) {
  return /^[A-Za-z\s.'-]+$/.test(String(value || "").trim());
}

function isAlphanumericAddress(value) {
  return /^[A-Za-z0-9\s,./#-]+$/.test(String(value || "").trim());
}

function isValidOptionalPhone(phone) {
  const val = String(phone || "").trim();
  if (!val) return true;
  return /^\d{10}$/.test(val);
}

function getTodayDateString() {
  return new Date().toISOString().split("T")[0];
}



export default function AddNewEmployee() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [roleOptions, setRoleOptions] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const [cityOptions, setCityOptions] = useState([]);
  const [onboardingCityOptions, setOnboardingCityOptions] = useState([]);

  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showOnboardingCityDropdown, setShowOnboardingCityDropdown] = useState(false);

  const [popup, setPopup] = useState({
    open: false,
    type: "error", // error | success
    title: "",
    message: "",
  });

  const showPopup = ({ type = "error", title = "", message = "" }) => {
    setPopup({
      open: true,
      type,
      title,
      message,
    });
  };

  const closePopup = () => {
    setPopup((prev) => ({ ...prev, open: false }));
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    dob: "",
    avatar: "",
    department: "",
    bio: "",

    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    pincode: "",

    designation: "",
    notes: "",

    userType: "admin_employee",
    role: "",
    roleId: "",

    canAdminPanel: true,
    canAccessAdminDashboard: true,
    canAccessWarehouseDashboard: false,
    canSwitchToAdminDashboard: false,
    canSwitchToWarehouseDashboard: false,

    warehouseAccessMode: "none",
    assignedWarehouseId: "",
    allowedWarehouseIds: [],

    permissionIds: [],
  });

  const [onboardingForm, setOnboardingForm] = useState({
    emergencyName: "",
    emergencyPhone: "",
    emergencyRelation: "",

    ref1Name: "",
    ref1Phone: "",
    ref1Relation: "",

    ref2Name: "",
    ref2Phone: "",
    ref2Relation: "",

    onboardingAddressLine1: "",
    onboardingAddressLine2: "",
    onboardingCity: "",
    onboardingState: "",
    onboardingCountry: "",
    onboardingPincode: "",

    excelKnowledgeConfirmed: false,
    onboardingStep: 0,
    isOnboardingComplete: false,
    onboardingNotes: "",
  });

  const [uploadFiles, setUploadFiles] = useState({
    profilePhoto: null,
    aadhaar: null,
    pan: null,
    photo1: null,
    photo2: null,
  });

  const updateOnboardingField = async (key, value) => {
    if (key === "onboardingPincode") {
      const pin = String(value || "").replace(/\D/g, "").slice(0, 6);

      setOnboardingForm((prev) => ({ ...prev, onboardingPincode: pin }));

      if (pin.length === 6) {
        try {
          const rows = await locationService.getPincodeDetails({ pincode: pin });
          const first = Array.isArray(rows) ? rows[0] : null;

          if (first) {
            const city =
              first.city ||
              first.district ||
              first.region ||
              first.taluk ||
              "";

            const state = first.state || "";
            const country =
              first.country === "IN" || first.country === "India"
                ? "India"
                : first.country || "India";

            setOnboardingForm((prev) => ({
              ...prev,
              onboardingPincode: pin,
              onboardingCity: city || prev.onboardingCity,
              onboardingState: state || prev.onboardingState,
              onboardingCountry: country || prev.onboardingCountry,
            }));
          }
        } catch (err) {
          console.error("Onboarding pincode lookup failed", err);
        }
      }

      return;
    }

    setOnboardingForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setUploadFiles((prev) => ({
      ...prev,
      [name]: files?.[0] || null,
    }));
  };

  const adminRoleOptions = useMemo(() => {
    return roleOptions.filter((r) => {
      const applies = String(r.appliesTo || "").toLowerCase();
      const key = String(r.name || "").toLowerCase();
      return (
        applies === "admin_employee" ||
        applies === "both" ||
        key.includes("admin")
      );
    });
  }, [roleOptions]);

  const warehouseRoleOptions = useMemo(() => {
    return roleOptions.filter((r) => {
      const applies = String(r.appliesTo || "").toLowerCase();
      const key = String(r.name || "").toLowerCase();
      return (
        applies === "warehouse_employee" ||
        applies === "both" ||
        key.includes("warehouse")
      );
    });
  }, [roleOptions]);

  const visibleRoles =
    form.userType === "warehouse_employee"
      ? warehouseRoleOptions
      : adminRoleOptions;


  function isWarehousePermissionOption(p = {}) {
    const key = String(p.key || p.permKey || "").trim().toLowerCase();
    const appliesTo = String(p.appliesTo || p.applies_to || "").trim().toLowerCase();

    return (
      appliesTo === "warehouse_employee" ||
      key.startsWith("wh.") ||
      key.startsWith("warehouse.")
    );
  }

  function isAdminPermissionOption(p = {}) {
    const key = String(p.key || p.permKey || "").trim().toLowerCase();
    const appliesTo = String(p.appliesTo || p.applies_to || "").trim().toLowerCase();

    return (
      appliesTo === "admin_employee" ||
      appliesTo === "both" ||
      key.startsWith("admin.") ||
      key.startsWith("rbac.") ||
      key === "dashboard.view" ||
      key.startsWith("employees.") ||
      key.startsWith("products.") ||
      key.startsWith("warehouses.")
    ) && !isWarehousePermissionOption(p);
  }

  const adminPanelPermissionOptions = useMemo(() => {
    return permissionOptions
      .filter(isAdminPermissionOption)
      .map((p) => ({
        value: p.id,
        label: getFriendlyPermissionLabel(p),
        description: p.description,
        key: p.key,
      }));
  }, [permissionOptions]);

  const warehousePanelPermissionOptions = useMemo(() => {
    return permissionOptions
      .filter(isWarehousePermissionOption)
      .map((p) => ({
        value: p.id,
        label: getFriendlyPermissionLabel(p),
        description: p.description,
        key: p.key,
      }));
  }, [permissionOptions]);


  const warehousePermissionIdSet = useMemo(() => {
    return new Set(warehousePanelPermissionOptions.map((p) => p.value));
  }, [warehousePanelPermissionOptions]);

  const adminPermissionIdSet = useMemo(() => {
    return new Set(adminPanelPermissionOptions.map((p) => p.value));
  }, [adminPanelPermissionOptions]);

  const permissionKeyToIdMap = useMemo(() => {
    const map = new Map();
    permissionOptions.forEach((p) => {
      const key = String(p.key || "").trim();
      const id = String(p.id || "").trim();
      if (key && id) map.set(key, id);
    });
    return map;
  }, [permissionOptions]);

  const warehouseSelectOptions = useMemo(() => {
    return warehouseOptions.map((w) => ({
      value: w.id,
      label: w.name,
    }));
  }, [warehouseOptions]);

  const selectedWarehousePermissionIds = useMemo(() => {
    return form.permissionIds.filter((id) => warehousePermissionIdSet.has(id));
  }, [form.permissionIds, warehousePermissionIdSet]);

  const selectedAdminPermissionIds = useMemo(() => {
    return form.permissionIds.filter((id) => adminPermissionIdSet.has(id));
  }, [form.permissionIds, adminPermissionIdSet]);

  const updateField = async (key, value) => {
    if (key === "pincode") {
      const pin = String(value || "").replace(/\D/g, "").slice(0, 6);

      setForm((prev) => ({ ...prev, pincode: pin }));

      if (pin.length === 6) {
        try {
          const rows = await locationService.getPincodeDetails({ pincode: pin });
          const first = Array.isArray(rows) ? rows[0] : null;

          if (first) {
            const city =
              first.city ||
              first.district ||
              first.region ||
              first.taluk ||
              "";

            const state = first.state || "";
            const country =
              first.country === "IN" || first.country === "India"
                ? "India"
                : first.country || "India";

            setForm((prev) => ({
              ...prev,
              pincode: pin,
              city: city || prev.city,
              state: state || prev.state,
              country: country || prev.country,
            }));
          }
        } catch (err) {
          console.error("Pincode lookup failed", err);
        }
      }

      return;
    }

    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const replaceAdminPermissionIds = (vals) => {
    setForm((prev) => {
      const warehouseSelected = prev.permissionIds.filter((id) =>
        warehousePermissionIdSet.has(id)
      );
      return {
        ...prev,
        permissionIds: [...new Set([...warehouseSelected, ...vals])],
      };
    });
  };

  const filteredCityOptions = useMemo(() => {
    const q = String(form.city || "").trim().toLowerCase();
    if (!q) return cityOptions.slice(0, 8);
    return cityOptions
      .filter((city) => city.toLowerCase().includes(q))
      .slice(0, 8);
  }, [cityOptions, form.city]);

  const filteredOnboardingCityOptions = useMemo(() => {
    const q = String(onboardingForm.onboardingCity || "").trim().toLowerCase();
    if (!q) return onboardingCityOptions.slice(0, 8);
    return onboardingCityOptions
      .filter((city) => city.toLowerCase().includes(q))
      .slice(0, 8);
  }, [onboardingCityOptions, onboardingForm.onboardingCity]);

  const replaceWarehousePermissionIds = (vals) => {
    setForm((prev) => {
      const adminSelected = prev.permissionIds.filter((id) =>
        !warehousePermissionIdSet.has(id)
      );
      return {
        ...prev,
        permissionIds: [...new Set([...adminSelected, ...vals])],
      };
    });
  };

  //location

  useEffect(() => {
    const stateName = String(onboardingForm.onboardingState || "").trim();
    const countryName =
      String(onboardingForm.onboardingCountry || "").trim().toUpperCase() === "IN"
        ? "India"
        : onboardingForm.onboardingCountry;

    if (!stateName) {
      setOnboardingCityOptions([]);
      return;
    }

    let active = true;

    (async () => {
      try {
        const rows = await locationService.searchCities({ countryName, stateName });
        if (!active) return;

        const names = rows
          .map((x) => x?.name || x?.city_name || x?.city || "")
          .filter(Boolean);

        setOnboardingCityOptions([...new Set(names)]);
      } catch {
        if (active) setOnboardingCityOptions([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [onboardingForm.onboardingState, onboardingForm.onboardingCountry]);

  useEffect(() => {
    const stateName = String(form.state || "").trim();
    const countryName =
      String(form.country || "").trim().toUpperCase() === "IN" ? "India" : form.country;

    if (!stateName) {
      setCityOptions([]);
      return;
    }

    let active = true;

    (async () => {
      try {
        const rows = await locationService.searchCities({ countryName, stateName });
        if (!active) return;

        const names = rows
          .map((x) => x?.name || x?.city_name || x?.city || "")
          .filter(Boolean);

        setCityOptions([...new Set(names)]);
      } catch {
        if (active) setCityOptions([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [form.state, form.country]);

  useEffect(() => {
    let cancelled = false;

    async function loadMeta() {
      try {
        const [rolesRes, permsRes, warehousesRes] = await Promise.all([
          EmployeesService.getRolesMeta?.().catch(() => []),
          EmployeesService.getPermissionsMeta?.().catch(() => []),
          warehouseService.listWarehouses?.({ page: 1, limit: 200 }).catch(() => []),
        ]);

        const roles = normalizeArray(rolesRes).map(normalizeRoleOption).filter((r) => r.id);
        const perms = normalizeArray(permsRes)
          .map(normalizePermissionOption)
          .filter((p) => p.id);
        const warehouses = normalizeArray(warehousesRes)
          .map(normalizeWarehouseOption)
          .filter((w) => w.id);

        if (cancelled) return;

        setRoleOptions(roles);
        setPermissionOptions(perms);
        setWarehouseOptions(warehouses);
      } catch (e) {
        console.error("Failed to load employee form meta", e);
      }
    }

    loadMeta();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    let cancelled = false;

    async function loadEmployee() {
      try {
        const emp = await EmployeesService.getEmployeeById(id);
        if (!emp || cancelled) return;

        setForm((prev) => ({
          ...prev,
          name: emp.name || "",
          email: emp.email || "",
          phone: emp.phone || "",
          gender: emp.gender || "",
          dob: emp?._raw?.dob ? String(emp._raw.dob).slice(0, 10) : "",
          avatar: emp.avatar || "",
          department: emp.department || "",
          bio: emp.bio || "",

          addressLine1: emp?.address?.line1 || "",
          addressLine2: emp?.address?.line2 || "",
          city: emp?.address?.city || "",
          state: emp?.address?.state || "",
          country: emp?.address?.country || "",
          pincode: emp?.address?.pincode || "",

          designation: emp?.profile?.designation || "",
          notes: emp?.profile?.notes || "",

          userType: emp.userType || "admin_employee",
          role: emp.role || "",
          roleId: emp.roleId || "",

          canAdminPanel: !!emp.canAdminPanel,
          canAccessAdminDashboard: !!emp.canAccessAdminDashboard,
          canAccessWarehouseDashboard: !!emp.canAccessWarehouseDashboard,
          canSwitchToAdminDashboard: !!emp.canSwitchToAdminDashboard,
          canSwitchToWarehouseDashboard: !!emp.canSwitchToWarehouseDashboard,

          warehouseAccessMode: emp.warehouseAccessMode || "none",
          assignedWarehouseId: emp.assignedWarehouseId || "",
          allowedWarehouseIds: Array.isArray(emp.allowedWarehouseIds)
            ? emp.allowedWarehouseIds
            : [],

          permissionIds: Array.isArray(emp.permissionIds) ? emp.permissionIds : [],
        }));

        const onboarding = emp?.onboardingData || {};
        const refs = Array.isArray(onboarding?.referenceContacts)
          ? onboarding.referenceContacts
          : [];
        setOnboardingForm({
          emergencyName: onboarding?.emergencyContact?.name || "",
          emergencyPhone: onboarding?.emergencyContact?.phone || "",
          emergencyRelation: onboarding?.emergencyContact?.relation || "",

          ref1Name: refs?.[0]?.name || "",
          ref1Phone: refs?.[0]?.phone || "",
          ref1Relation: refs?.[0]?.relation || "",

          ref2Name: refs?.[1]?.name || "",
          ref2Phone: refs?.[1]?.phone || "",
          ref2Relation: refs?.[1]?.relation || "",

          onboardingAddressLine1: onboarding?.address?.line1 || "",
          onboardingAddressLine2: onboarding?.address?.line2 || "",
          onboardingCity: onboarding?.address?.city || "",
          onboardingState: onboarding?.address?.state || "",
          onboardingCountry: onboarding?.address?.country || "",
          onboardingPincode: onboarding?.address?.pincode || "",

          excelKnowledgeConfirmed: !!onboarding?.excelKnowledgeConfirmed,
          onboardingStep: emp?.onboardingStep || 0,
          isOnboardingComplete: emp?.onboarding === "Yes",
          onboardingNotes: onboarding?.notes || "",
        });
      } catch (e) {
        alert(e?.message || "Failed to load employee");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  useEffect(() => {
    if (!visibleRoles.length) return;

    const exists = visibleRoles.some((r) => String(r.id) === String(form.roleId));
    if (exists) return;

    setForm((prev) => {
      if (!prev.roleId && !prev.role) return prev;
      return {
        ...prev,
        roleId: "",
        role: "",
      };
    });
  }, [form.roleId, form.role, visibleRoles]);

  useEffect(() => {
    setForm((prev) => {
      if (prev.userType === "admin_employee") {
        const hasWarehouse = prev.allowedWarehouseIds.length > 0;

        const next = {
          ...prev,
          canAdminPanel: true,
          canAccessAdminDashboard: true,
          canAccessWarehouseDashboard: hasWarehouse,
          canSwitchToAdminDashboard: hasWarehouse,
          canSwitchToWarehouseDashboard: hasWarehouse,
          warehouseAccessMode: hasWarehouse ? "selected" : "none",
          assignedWarehouseId: "",
        };

        const unchanged =
          prev.canAdminPanel === next.canAdminPanel &&
          prev.canAccessAdminDashboard === next.canAccessAdminDashboard &&
          prev.canAccessWarehouseDashboard === next.canAccessWarehouseDashboard &&
          prev.canSwitchToAdminDashboard === next.canSwitchToAdminDashboard &&
          prev.canSwitchToWarehouseDashboard === next.canSwitchToWarehouseDashboard &&
          prev.warehouseAccessMode === next.warehouseAccessMode &&
          prev.assignedWarehouseId === next.assignedWarehouseId;

        return unchanged ? prev : next;
      }

      const hasAdminAccess = !!prev.canAccessAdminDashboard;
      const hasWarehouse = !!prev.assignedWarehouseId;

      const next = {
        ...prev,
        canAdminPanel: hasAdminAccess,
        canAccessWarehouseDashboard: hasWarehouse,
        warehouseAccessMode: hasWarehouse ? "assigned" : "none",
        allowedWarehouseIds:
          prev.allowedWarehouseIds.length === 0 ? prev.allowedWarehouseIds : [],
        canSwitchToAdminDashboard: hasAdminAccess && hasWarehouse,
        canSwitchToWarehouseDashboard: hasAdminAccess && hasWarehouse,
      };

      const unchanged =
        prev.canAdminPanel === next.canAdminPanel &&
        prev.canAccessWarehouseDashboard === next.canAccessWarehouseDashboard &&
        prev.warehouseAccessMode === next.warehouseAccessMode &&
        prev.canSwitchToAdminDashboard === next.canSwitchToAdminDashboard &&
        prev.canSwitchToWarehouseDashboard === next.canSwitchToWarehouseDashboard &&
        prev.allowedWarehouseIds.length === next.allowedWarehouseIds.length;

      return unchanged ? prev : next;
    });
  }, [
    form.userType,
    form.allowedWarehouseIds.length,
    form.assignedWarehouseId,
    form.canAccessAdminDashboard,
  ]);

  const onRoleChange = (roleId) => {
    const found = visibleRoles.find((r) => String(r.id) === String(roleId));

    const rolePermissionIds = Array.isArray(found?.permissions)
      ? found.permissions
        .map((perm) => {
          const value = String(perm || "").trim();

          return (
            permissionKeyToIdMap.get(value) || // role has key
            permissionOptions.find((p) => String(p.id) === value)?.id || // role already has id
            null
          );
        })
        .filter(Boolean)
      : [];

    setForm((prev) => ({
      ...prev,
      roleId,
      role: prev.userType === "warehouse_employee" ? "warehouse_employee" : "admin_employee",
      selectedRoleName: found?.name || "",
      permissionIds: [...new Set(rolePermissionIds)],
    }));
  };

  const submitPayload = useMemo(() => {
    const base = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      gender: form.gender || undefined,
      dob: form.dob || undefined,
      avatar: form.avatar.trim() || undefined,
      department: form.department.trim(),
      bio: form.bio.trim() || undefined,

      profile: {
        designation: form.designation.trim(),
        notes: form.notes.trim(),
        bio: form.bio.trim(),
      },

      address: {
        line1: form.addressLine1.trim(),
        line2: form.addressLine2.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        country: form.country.trim(),
        pincode: form.pincode.trim(),
      },

      userType: form.userType,
      role: form.userType === "warehouse_employee" ? "warehouse_employee" : "admin_employee",
      roleId: form.roleId || undefined,

      canAdminPanel: !!form.canAdminPanel,
      canAccessAdminDashboard: !!form.canAccessAdminDashboard,
      canAccessWarehouseDashboard: !!form.canAccessWarehouseDashboard,
      canSwitchToAdminDashboard: !!form.canSwitchToAdminDashboard,
      canSwitchToWarehouseDashboard: !!form.canSwitchToWarehouseDashboard,

      warehouseAccessMode: form.warehouseAccessMode,
      assignedWarehouseId: form.assignedWarehouseId || null,
      allowedWarehouseIds: Array.isArray(form.allowedWarehouseIds)
        ? form.allowedWarehouseIds
        : [],
      permissionIds: Array.isArray(form.permissionIds) ? form.permissionIds : [],
    };

    if (form.userType === "admin_employee") {
      base.canAdminPanel = true;
      base.canAccessAdminDashboard = true;
      base.canAccessWarehouseDashboard = base.allowedWarehouseIds.length > 0;
      base.canSwitchToAdminDashboard = base.allowedWarehouseIds.length > 0;
      base.canSwitchToWarehouseDashboard = base.allowedWarehouseIds.length > 0;
      base.warehouseAccessMode = base.allowedWarehouseIds.length > 0 ? "selected" : "none";
      base.assignedWarehouseId = null;
    } else {
      base.canAccessWarehouseDashboard = !!base.assignedWarehouseId;
      base.warehouseAccessMode = base.assignedWarehouseId ? "assigned" : "none";
      base.allowedWarehouseIds = [];
      if (!base.canAccessAdminDashboard) {
        base.canAdminPanel = false;
      }
    }

    return base;
  }, [form]);

  const submitOnboardingUpdate = async () => {
    if (!isEdit) return;

    const formData = new FormData();

    formData.append("onboardingStep", onboardingForm.onboardingStep);
    formData.append("isOnboardingComplete", onboardingForm.isOnboardingComplete);

    formData.append(
      "emergencyContact",
      JSON.stringify({
        name: onboardingForm.emergencyName,
        phone: onboardingForm.emergencyPhone,
        relation: onboardingForm.emergencyRelation,
      })
    );

    formData.append(
      "referenceContacts",
      JSON.stringify([
        {
          name: onboardingForm.ref1Name,
          phone: onboardingForm.ref1Phone,
          relation: onboardingForm.ref1Relation,
        },
        {
          name: onboardingForm.ref2Name,
          phone: onboardingForm.ref2Phone,
          relation: onboardingForm.ref2Relation,
        },
      ])
    );

    formData.append("excelKnowledgeConfirmed", onboardingForm.excelKnowledgeConfirmed);

    formData.append(
      "onboarding",
      JSON.stringify({
        notes: onboardingForm.onboardingNotes,
        address: {
          line1: onboardingForm.onboardingAddressLine1,
          line2: onboardingForm.onboardingAddressLine2,
          city: onboardingForm.onboardingCity,
          state: onboardingForm.onboardingState,
          country: onboardingForm.onboardingCountry,
          pincode: onboardingForm.onboardingPincode,
        },
      })
    );

    Object.entries(uploadFiles).forEach(([key, file]) => {
      if (file) {
        formData.append(`documents.${key}`, file);
      }
    });

    await EmployeesService.updateEmployeeOnboarding(id, formData);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const today = getTodayDateString();

    if (!submitPayload.name) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Full Name is required.",
      });
    }

    if (!isAlphabeticText(submitPayload.name)) {
      return showPopup({
        type: "error",
        title: "Invalid Full Name",
        message: "Full Name should contain only letters and spaces.",
      });
    }

    if (!submitPayload.email) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Email is required.",
      });
    }

    if (!isValidEmail(submitPayload.email)) {
      return showPopup({
        type: "error",
        title: "Invalid Email",
        message: "Please enter a valid email address.",
      });
    }

    if (!submitPayload.phone) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Phone is required.",
      });
    }

    if (!isValidPhone(submitPayload.phone)) {
      return showPopup({
        type: "error",
        title: "Invalid Phone",
        message: "Phone number must be exactly 10 digits.",
      });
    }

    if (!submitPayload.gender) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Gender is required.",
      });
    }

    if (!submitPayload.dob) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "DOB is required.",
      });
    }

    if (submitPayload.dob > today) {
      return showPopup({
        type: "error",
        title: "Invalid DOB",
        message: "DOB cannot be a future date.",
      });
    }

    if (!submitPayload.department) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Department is required.",
      });
    }

    if (!isAlphabeticText(submitPayload.department)) {
      return showPopup({
        type: "error",
        title: "Invalid Department",
        message: "Department should contain only letters and spaces.",
      });
    }

    if (!submitPayload.address.line1) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Address Line 1 is required.",
      });
    }

    if (!isAlphanumericAddress(submitPayload.address.line1)) {
      return showPopup({
        type: "error",
        title: "Invalid Address",
        message: "Address Line 1 contains invalid characters.",
      });
    }

    if (submitPayload.address.line2 && !isAlphanumericAddress(submitPayload.address.line2)) {
      return showPopup({
        type: "error",
        title: "Invalid Address",
        message: "Address Line 2 contains invalid characters.",
      });
    }

    if (!submitPayload.address.pincode) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Pincode is required.",
      });
    }

    if (!isValidPincode(submitPayload.address.pincode)) {
      return showPopup({
        type: "error",
        title: "Invalid Pincode",
        message: "Pincode must be exactly 6 digits.",
      });
    }

    if (!submitPayload.address.city) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "City is required.",
      });
    }

    if (!String(submitPayload.address.city || "").trim()) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "City is required.",
      });
    }

    if (!submitPayload.address.state) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "State is required.",
      });
    }

    if (!String(submitPayload.address.state || "").trim()) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "State is required.",
      });
    }

    if (!String(submitPayload.address.country || "").trim()) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Country is required.",
      });
    }

    if (!isAlphabeticText(submitPayload.address.country)) {
      return showPopup({
        type: "error",
        title: "Invalid Country",
        message: "Country should contain only letters and spaces.",
      });
    }

    if (!submitPayload.userType) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "User Type is required.",
      });
    }

    if (!submitPayload.roleId) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Role is required.",
      });
    }

    if (
      submitPayload.userType === "warehouse_employee" &&
      !submitPayload.assignedWarehouseId
    ) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "Assigned Warehouse is required.",
      });
    }

    if (!submitPayload.permissionIds || submitPayload.permissionIds.length === 0) {
      return showPopup({
        type: "error",
        title: "Required Field Missing",
        message: "At least one permission is required.",
      });
    }

    if (
      onboardingForm.emergencyName &&
      !isAlphabeticText(onboardingForm.emergencyName)
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Emergency Name",
        message: "Emergency Name should contain only letters and spaces.",
      });
    }

    if (onboardingForm.emergencyRelation && !isAlphabeticText(onboardingForm.emergencyRelation)) {
      return showPopup({
        type: "error",
        title: "Invalid Emergency Relation",
        message: "Emergency Relation should contain only letters and spaces.",
      });
    }

    if (onboardingForm.ref1Name && !isAlphabeticText(onboardingForm.ref1Name)) {
      return showPopup({
        type: "error",
        title: "Invalid Reference 1 Name",
        message: "Reference 1 Name should contain only letters and spaces.",
      });
    }

    if (onboardingForm.ref1Relation && !isAlphabeticText(onboardingForm.ref1Relation)) {
      return showPopup({
        type: "error",
        title: "Invalid Reference 1 Relation",
        message: "Reference 1 Relation should contain only letters and spaces.",
      });
    }

    if (onboardingForm.ref2Name && !isAlphabeticText(onboardingForm.ref2Name)) {
      return showPopup({
        type: "error",
        title: "Invalid Reference 2 Name",
        message: "Reference 2 Name should contain only letters and spaces.",
      });
    }

    if (onboardingForm.ref2Relation && !isAlphabeticText(onboardingForm.ref2Relation)) {
      return showPopup({
        type: "error",
        title: "Invalid Reference 2 Relation",
        message: "Reference 2 Relation should contain only letters and spaces.",
      });
    }

    if (!isValidOptionalPhone(onboardingForm.emergencyPhone)) {
      return showPopup({
        type: "error",
        title: "Invalid Emergency Phone",
        message: "Emergency Phone must be exactly 10 digits.",
      });
    }

    if (!isValidOptionalPhone(onboardingForm.ref1Phone)) {
      return showPopup({
        type: "error",
        title: "Invalid Reference 1 Phone",
        message: "Reference 1 Phone must be exactly 10 digits.",
      });
    }

    if (!isValidOptionalPhone(onboardingForm.ref2Phone)) {
      return showPopup({
        type: "error",
        title: "Invalid Reference 2 Phone",
        message: "Reference 2 Phone must be exactly 10 digits.",
      });
    }

    if (
      onboardingForm.onboardingAddressLine1 &&
      !isAlphanumericAddress(onboardingForm.onboardingAddressLine1)
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding Address",
        message: "Onboarding Address Line 1 contains invalid characters.",
      });
    }

    if (
      onboardingForm.onboardingAddressLine2 &&
      !isAlphanumericAddress(onboardingForm.onboardingAddressLine2)
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding Address",
        message: "Onboarding Address Line 2 contains invalid characters.",
      });
    }

    if (
      onboardingForm.onboardingCity &&
      !String(onboardingForm.onboardingCity || "").trim()
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding City",
        message: "Onboarding City is invalid.",
      });
    }

    if (
      onboardingForm.onboardingState &&
      !String(onboardingForm.onboardingState || "").trim()
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding State",
        message: "Onboarding State is invalid.",
      });
    }

    if (
      onboardingForm.onboardingCountry &&
      !String(onboardingForm.onboardingCountry || "").trim()
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding Country",
        message: "Onboarding Country is invalid.",
      });
    }

    if (
      onboardingForm.onboardingPincode &&
      !isValidPincode(onboardingForm.onboardingPincode)
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding Pincode",
        message: "Onboarding Pincode must be exactly 6 digits.",
      });
    }

    if (
      onboardingForm.onboardingStep !== "" &&
      (Number(onboardingForm.onboardingStep) < 0 || Number.isNaN(Number(onboardingForm.onboardingStep)))
    ) {
      return showPopup({
        type: "error",
        title: "Invalid Onboarding Step",
        message: "Onboarding Step must be zero or more.",
      });
    }

    try {
      setSaving(true);

      if (isEdit) {
        await EmployeesService.updateEmployee(id, submitPayload);
        await submitOnboardingUpdate();

        showPopup({
          type: "success",
          title: "Success",
          message: "Employee updated successfully.",
        });
      } else {
        await EmployeesService.createEmployee(submitPayload);

        showPopup({
          type: "success",
          title: "Success",
          message: "Employee created successfully.",
        });
      }

      setTimeout(() => {
        navigate("/admin/dashboard/employees");
      }, 1200);
    } catch (e) {
      showPopup({
        type: "error",
        title: "Failed",
        message: e?.message || "Failed to save employee.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-[14px] text-slate-500">Loading employee...</div>;
  }

  return (
    <div className="space-y-5">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/admin/dashboard/employees")}
          className="inline-flex h-[38px] w-[52px] items-center justify-center rounded-[10px] border bg-white text-[#d86a78] transition hover:bg-[#fff8f9]"
          style={{ borderColor: BORDER }}
        >
          <ArrowLeft size={16} />
        </button>

        <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
          {isEdit ? "EDIT EMPLOYEE" : "ADD NEW EMPLOYEE"}
        </h1>
      </div>

      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Card className="p-5">
          <div className="mb-4 text-[16px] font-bold text-slate-800">Basic Details</div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Input
              label="Full Name"
              required
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="Enter employee name"
            />

            <Input
              label="Email"
              required
              type="text"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              placeholder="Enter email"
              disabled={isEdit}
            />

            <Input
              label="Phone"
              required
              value={form.phone}
              onChange={(e) =>
                updateField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              placeholder="Enter phone"
              maxLength={10}
            />

            <Select
              label="Gender"
              required
              value={form.gender}
              onChange={(e) => updateField("gender", e.target.value)}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </Select>

            <Input
              label="DOB"
              required
              type="date"
              value={form.dob}
              onChange={(e) => updateField("dob", e.target.value)}
            />

            <Input
              label="Department"
              required
              value={form.department}
              onChange={(e) => updateField("department", e.target.value)}
              placeholder="Enter department"
            />

            {/* <div className="md:col-span-2 xl:col-span-3">
              <Input
                label="Avatar URL"
                value={form.avatar}
                onChange={(e) => updateField("avatar", e.target.value)}
                placeholder="https://example.com/avatar.png"
              />
            </div> */}

            <div className="md:col-span-2 xl:col-span-3">
              <Textarea
                label="Bio"
                value={form.bio}
                onChange={(e) => updateField("bio", e.target.value)}
                placeholder="Enter bio"
              />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4 text-[16px] font-bold text-slate-800">Address</div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="md:col-span-2 xl:col-span-3">
              <Input
                label="Address Line 1"
                required
                value={form.addressLine1}
                onChange={(e) => updateField("addressLine1", e.target.value)}
                placeholder="Enter address line 1"
              />
            </div>

            <div className="md:col-span-2 xl:col-span-3">
              <Input
                label="Address Line 2"
                value={form.addressLine2}
                onChange={(e) => updateField("addressLine2", e.target.value)}
                placeholder="Enter address line 2"
              />
            </div>

            <Input
              label="Pincode"
              required
              value={form.pincode}
              onChange={(e) => updateField("pincode", e.target.value)}
              placeholder="Enter pincode"
              maxLength={6}
            />

            <Input
              label="State"
              required
              value={form.state}
              onChange={(e) => updateField("state", e.target.value)}
              placeholder="Enter state"
            />

            <Input
              label="Country"
              required
              value={form.country}
              onChange={(e) => updateField("country", e.target.value)}
              placeholder="Enter country"
            />

            <div className="relative">
              <Input
                label="City"
                required
                value={form.city}
                onChange={(e) => {
                  updateField("city", e.target.value);
                  setShowCityDropdown(true);
                }}
                onFocus={() => setShowCityDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowCityDropdown(false), 150);
                }}
                placeholder="Enter or select city"
                autoComplete="off"
              />

              {showCityDropdown && filteredCityOptions.length > 0 ? (
                <div
                  className="absolute left-0 right-0 top-[72px] z-20 max-h-56 overflow-auto rounded-[10px] border bg-white shadow-lg"
                  style={{ borderColor: BORDER }}
                >
                  {filteredCityOptions.map((city) => (
                    <button
                      key={city}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-[14px] text-slate-700 hover:bg-slate-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        updateField("city", city);
                        setShowCityDropdown(false);
                      }}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4 text-[16px] font-bold text-slate-800">Profile</div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Designation"
              value={form.designation}
              onChange={(e) => updateField("designation", e.target.value)}
              placeholder="Enter designation"
            />

            <div className="md:col-span-2">
              <Textarea
                label="Notes"
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Enter notes"
              />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4 text-[16px] font-bold text-slate-800">
            Onboarding & Documents
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Input
              label="Emergency Name"
              value={onboardingForm.emergencyName}
              onChange={(e) => updateOnboardingField("emergencyName", e.target.value)}
            />
            <Input
              label="Emergency Phone"
              value={onboardingForm.emergencyPhone}
              onChange={(e) =>
                updateOnboardingField(
                  "emergencyPhone",
                  e.target.value.replace(/\D/g, "").slice(0, 10)
                )
              }
              maxLength={10}
            />
            <Input
              label="Emergency Relation"
              value={onboardingForm.emergencyRelation}
              onChange={(e) => updateOnboardingField("emergencyRelation", e.target.value)}
            />

            <Input
              label="Reference 1 Name"
              value={onboardingForm.ref1Name}
              onChange={(e) => updateOnboardingField("ref1Name", e.target.value)}
            />
            <Input
              label="Reference 1 Phone"
              value={onboardingForm.ref1Phone}
              onChange={(e) =>
                updateOnboardingField(
                  "ref1Phone",
                  e.target.value.replace(/\D/g, "").slice(0, 10)
                )
              }
              maxLength={10}
            />
            <Input
              label="Reference 1 Relation"
              value={onboardingForm.ref1Relation}
              onChange={(e) => updateOnboardingField("ref1Relation", e.target.value)}
            />

            <Input
              label="Reference 2 Name"
              value={onboardingForm.ref2Name}
              onChange={(e) => updateOnboardingField("ref2Name", e.target.value)}
            />
            <Input
              label="Reference 2 Phone"
              value={onboardingForm.ref2Phone}
              onChange={(e) =>
                updateOnboardingField(
                  "ref2Phone",
                  e.target.value.replace(/\D/g, "").slice(0, 10)
                )
              }
              maxLength={10}
            />
            <Input
              label="Reference 2 Relation"
              value={onboardingForm.ref2Relation}
              onChange={(e) => updateOnboardingField("ref2Relation", e.target.value)}
            />

            <div className="md:col-span-2 xl:col-span-3">
              <Input
                label="Onboarding Address Line 1"
                value={onboardingForm.onboardingAddressLine1}
                onChange={(e) => updateOnboardingField("onboardingAddressLine1", e.target.value)}
              />
            </div>

            <div className="md:col-span-2 xl:col-span-3">
              <Input
                label="Onboarding Address Line 2"
                value={onboardingForm.onboardingAddressLine2}
                onChange={(e) => updateOnboardingField("onboardingAddressLine2", e.target.value)}
              />
            </div>

            <Input
              label="Onboarding Pincode"
              value={onboardingForm.onboardingPincode}
              onChange={(e) => updateOnboardingField("onboardingPincode", e.target.value)}
              placeholder="Enter pincode"
              maxLength={6}
            />

            <Input
              label="Onboarding State"
              value={onboardingForm.onboardingState}
              onChange={(e) => updateOnboardingField("onboardingState", e.target.value)}
            />

            <Input
              label="Onboarding Country"
              value={onboardingForm.onboardingCountry}
              onChange={(e) => updateOnboardingField("onboardingCountry", e.target.value)}
            />

            <div className="relative">
              <Input
                label="Onboarding City"
                value={onboardingForm.onboardingCity}
                onChange={(e) => {
                  updateOnboardingField("onboardingCity", e.target.value);
                  setShowOnboardingCityDropdown(true);
                }}
                onFocus={() => setShowOnboardingCityDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowOnboardingCityDropdown(false), 150);
                }}
                placeholder="Enter or select city"
                autoComplete="off"
              />

              {showOnboardingCityDropdown && filteredOnboardingCityOptions.length > 0 ? (
                <div
                  className="absolute left-0 right-0 top-[72px] z-20 max-h-56 overflow-auto rounded-[10px] border bg-white shadow-lg"
                  style={{ borderColor: BORDER }}
                >
                  {filteredOnboardingCityOptions.map((city) => (
                    <button
                      key={city}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-[14px] text-slate-700 hover:bg-slate-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        updateOnboardingField("onboardingCity", city);
                        setShowOnboardingCityDropdown(false);
                      }}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <Input
              label="Onboarding Step"
              type="number"
              min="0"
              value={onboardingForm.onboardingStep}
              onChange={(e) => updateOnboardingField("onboardingStep", e.target.value)}
            />

            <label className="block">
              <div className="mb-1 text-[13px] font-semibold text-slate-600">
                Profile Photo
              </div>
              <input type="file" name="profilePhoto" onChange={handleFileChange} />
            </label>

            <label className="block">
              <div className="mb-1 text-[13px] font-semibold text-slate-600">
                Aadhaar
              </div>
              <input type="file" name="aadhaar" onChange={handleFileChange} />
            </label>

            <label className="block">
              <div className="mb-1 text-[13px] font-semibold text-slate-600">
                PAN
              </div>
              <input type="file" name="pan" onChange={handleFileChange} />
            </label>

            <label className="block">
              <div className="mb-1 text-[13px] font-semibold text-slate-600">
                Photo 1
              </div>
              <input type="file" name="photo1" onChange={handleFileChange} />
            </label>

            <label className="block">
              <div className="mb-1 text-[13px] font-semibold text-slate-600">
                Photo 2
              </div>
              <input type="file" name="photo2" onChange={handleFileChange} />
            </label>

            <div className="md:col-span-2 xl:col-span-3">
              <Textarea
                label="Onboarding Notes"
                value={onboardingForm.onboardingNotes}
                onChange={(e) => updateOnboardingField("onboardingNotes", e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-[14px] text-slate-700">
              <input
                type="checkbox"
                checked={onboardingForm.excelKnowledgeConfirmed}
                onChange={(e) =>
                  updateOnboardingField("excelKnowledgeConfirmed", e.target.checked)
                }
              />
              Excel Knowledge Confirmed
            </label>

            <label className="flex items-center gap-2 text-[14px] text-slate-700">
              <input
                type="checkbox"
                checked={onboardingForm.isOnboardingComplete}
                onChange={(e) =>
                  updateOnboardingField("isOnboardingComplete", e.target.checked)
                }
              />
              Onboarding Complete
            </label>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4 text-[16px] font-bold text-slate-800">Access Configuration</div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Select
              label="User Type"
              required
              value={form.userType}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  userType: e.target.value,
                  role: "",
                  roleId: "",
                  permissionIds: [],
                  assignedWarehouseId: "",
                  allowedWarehouseIds: [],
                  warehouseAccessMode: e.target.value === "warehouse_employee" ? "assigned" : "none",
                  canAdminPanel: e.target.value === "admin_employee",
                  canAccessAdminDashboard: e.target.value === "admin_employee",
                  canAccessWarehouseDashboard: false,
                  canSwitchToAdminDashboard: false,
                  canSwitchToWarehouseDashboard: false,
                }))
              }
            >
              <option value="admin_employee">Admin Employee</option>
              <option value="warehouse_employee">Warehouse Employee</option>
            </Select>

            <Select
              label="Role"
              required
              value={form.roleId}
              onChange={(e) => onRoleChange(e.target.value)}
            >
              <option value="">Select role</option>
              {visibleRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </div>

          {form.userType === "admin_employee" ? (
            <div className="mt-4 space-y-4">
              <Toggle
                label="Admin Panel Access"
                checked
                disabled
                onChange={() => { }}
                hint="Admin employee always has admin panel access."
              />

              <ModulePermissionPicker
                label="Admin Panel Permissions"
                options={adminPanelPermissionOptions}
                selected={selectedAdminPermissionIds}
                onChange={replaceAdminPermissionIds}
                modules={ADMIN_PERMISSION_MODULES}
                hint="Select module-wise access for the admin employee."
              />

              <Toggle
                label="Warehouse Dashboard Access"
                checked={form.canAccessWarehouseDashboard}
                onChange={(checked) => {
                  if (!checked) {
                    setForm((prev) => {
                      const adminSelected = prev.permissionIds.filter(
                        (pid) => !warehousePermissionIdSet.has(pid)
                      );

                      return {
                        ...prev,
                        canAccessWarehouseDashboard: false,
                        allowedWarehouseIds: [],
                        warehouseAccessMode: "none",
                        permissionIds: adminSelected,
                      };
                    });
                  } else {
                    setForm((prev) => ({
                      ...prev,
                      canAccessWarehouseDashboard: true,
                      warehouseAccessMode:
                        prev.allowedWarehouseIds.length > 0 ? "selected" : "none",
                    }));
                  }
                }}
                hint="Assign one or more warehouses if this admin employee should access warehouse dashboard."
              />

              {form.canAccessWarehouseDashboard ? (
                <>
                  <MultiCheckbox
                    label="Assign Warehouses"
                    options={warehouseSelectOptions}
                    selected={form.allowedWarehouseIds}
                    onChange={(vals) =>
                      setForm((prev) => ({
                        ...prev,
                        allowedWarehouseIds: vals,
                        warehouseAccessMode: vals.length > 0 ? "selected" : "none",
                        canAccessWarehouseDashboard: vals.length > 0,
                      }))
                    }
                    hint="Select which warehouses this admin employee can open."
                  />

                  <ModulePermissionPicker
                    label="Warehouse Permissions"
                    options={warehousePanelPermissionOptions}
                    selected={selectedWarehousePermissionIds}
                    onChange={replaceWarehousePermissionIds}
                    modules={WAREHOUSE_PERMISSION_MODULES}
                    hint="Select module-wise access for the warehouse dashboard."
                  />
                </>
              ) : null}
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <Select
                label="Assigned Warehouse"
                required
                value={form.assignedWarehouseId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignedWarehouseId: e.target.value,
                    warehouseAccessMode: e.target.value ? "assigned" : "none",
                    canAccessWarehouseDashboard: !!e.target.value,
                  }))
                }
              >
                <option value="">Select warehouse</option>
                {warehouseSelectOptions.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </Select>

              <ModulePermissionPicker
                label="Warehouse Permissions"
                options={warehousePanelPermissionOptions}
                selected={selectedWarehousePermissionIds}
                onChange={replaceWarehousePermissionIds}
                modules={WAREHOUSE_PERMISSION_MODULES}
                hint="Select module-wise access for the warehouse employee."
              />

              <Toggle
                label="Admin Panel Access"
                checked={form.canAccessAdminDashboard}
                onChange={(checked) =>
                  setForm((prev) => {
                    const warehouseSelected = prev.permissionIds.filter((pid) =>
                      warehousePermissionIdSet.has(pid)
                    );

                    return {
                      ...prev,
                      canAccessAdminDashboard: checked,
                      canAdminPanel: checked,
                      permissionIds: checked ? prev.permissionIds : warehouseSelected,
                    };
                  })
                }
                hint="Turn on if this warehouse employee should also access admin panel."
              />

              {form.canAccessAdminDashboard ? (
                <ModulePermissionPicker
                  label="Admin Panel Permissions"
                  options={adminPanelPermissionOptions}
                  selected={selectedAdminPermissionIds}
                  onChange={replaceAdminPermissionIds}
                  modules={ADMIN_PERMISSION_MODULES}
                  hint="Select module-wise access for the admin panel."
                />
              ) : null}
            </div>
          )}
        </Card>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard/employees")}
            className="h-[42px] rounded-[10px] border px-5 text-[14px] font-semibold text-slate-600 hover:bg-slate-50"
            style={{ borderColor: BORDER }}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={saving}
            className="h-[42px] rounded-[10px] px-5 text-[14px] font-semibold text-white"
            style={{ backgroundColor: saving ? "#e3aab2" : PINK }}
            onMouseEnter={(e) => {
              if (!saving) e.currentTarget.style.backgroundColor = PINK_HOVER;
            }}
            onMouseLeave={(e) => {
              if (!saving) e.currentTarget.style.backgroundColor = PINK;
            }}
          >
            {saving ? "Saving..." : isEdit ? "Update Employee" : "Create Employee"}
          </button>
        </div>
      </form>

      <StatusPopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </div>
  );
}